import * as React from "react";
import { useState, useContext, useEffect } from "react";
import { Modal, Header, Grid, Button, TextArea, Icon } from "semantic-ui-react";
import Notifications, { notify } from 'react-notify-toast';
import { authentication } from "../../../../../../../authentication";
import { backend } from "../../../../../../../xconvert-backend";
import { IfBox } from "../../../../../../style/if";
import { CompanyHistory, Contract } from "../../../../../../../generated";
import { ConfigContext } from "../../../../../context/ConfigContext";
import { CustomDiff } from "../../../../configChange/configEdit/diff/CustomDiff";

export interface HistoryModalProps {
    isOpen: boolean
    onClose: (openRollback: boolean, commentDidChange: boolean) => void
    followingContractVersion: Contract
    history: CompanyHistory
}

function ContractHistoryModal(props: HistoryModalProps) {
    const context = useContext(ConfigContext);

    const [changeComment, setChangeComment] = useState(false);
    const [comment, setComment] = useState("");
    const [newComment, setNewComment] = useState("");
    const [commentDidChange, setCommentDidChange] = useState(false);

    useEffect(() => {
        if (props.history?.changeComment != null) {
            setComment(props.history.changeComment);
            setNewComment(props.history.changeComment);
        }
    }, [props.history]);

    function handleOpenRollback(openRollback: boolean) {
        props.onClose(openRollback, commentDidChange);
    }

    function handleClose() {
        props.onClose(false, commentDidChange);
    }

    function handleChangeComment() {
        setChangeComment(true);
    }

    async function saveCommentChange() {
        let auth = await backend.withTokenAuthHeader(authentication.token);
        let request = { newComment: newComment };
        let response = await backend.internalApi.updateCompanyConfigHistoryComment(
            context.companyId,
            props.history._id,
            request,
            auth
        );
        if (response.success) {
            notify.show('updated comment', 'success', 3000, '#28f751');
        } else {
            notify.show("ERROR: unable to change comment.", 'error', 5000, '#fc0303');
        }
        setChangeComment(false);
        setComment(newComment);
        setCommentDidChange(true);
    }

    function discardCommentChange() {
        setComment(props.history.changeComment);
        setChangeComment(false);
    }

    if (!props.history) {
        return <p></p>;
    }

    return (
        <Modal
            open={props.isOpen}
            onClose={handleClose}
            onMount={() => {
                if (props.history?.changeComment != null) {
                    setComment(props.history.changeComment);
                    setNewComment(props.history.changeComment);
                }
            }}
            dimmer="blurring"
            size='fullscreen'
        >
            <Header icon='browser' content='Contract History (change view)' />
            <Modal.Content>
                <p>This view shows what did actually change with this history entry. It compares this history-state with the previous history-state.</p>
                <Grid padded="horizontally" stackable columns='equal' centered>
                    <Grid.Row>
                        <Grid.Column>Id</Grid.Column>
                        <Grid.Column>{props.history._id}</Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>Changed By</Grid.Column>
                        <Grid.Column>{props.history.changedBy}</Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>Timestamp</Grid.Column>
                        <Grid.Column>{props.history.timestamp}</Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>changeMethod</Grid.Column>
                        <Grid.Column>{props.history.changeMethod}</Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>comment</Grid.Column>
                        <Grid.Column>
                            <IfBox shouldShow={!changeComment}>
                                {comment}
                                <Button icon onClick={handleChangeComment}><Icon name='pen square' /></Button>
                            </IfBox>
                            <IfBox shouldShow={changeComment}>
                                <TextArea
                                    id='CommentTextArea'
                                    onChange={event => setNewComment(event.currentTarget.value)}
                                    cols={200}
                                    value={newComment}
                                />
                                <Button icon onClick={saveCommentChange}><Icon name='save' /></Button>
                                <Button icon onClick={discardCommentChange}><Icon name='trash' /></Button>
                            </IfBox>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column><h2>BEFORE</h2></Grid.Column>
                        <Grid.Column><h2>{"--> compared to -->"}</h2></Grid.Column>
                        <Grid.Column><h2>AFTER</h2></Grid.Column>
                    </Grid.Row>
                </Grid>
                <CustomDiff
                    oldJson={JSON.stringify(props.history.contract, null, 4)}
                    newJson={JSON.stringify(props.followingContractVersion, null, 4)}
                />
                <Button primary onClick={() => handleOpenRollback(true)}>rollback</Button>
                <Button onClick={handleClose}>cancel</Button>
                <Notifications />
            </Modal.Content>
        </Modal>
    );
}

export { ContractHistoryModal };
